import React, { useEffect, useRef, useState } from 'react'
import { FilePond } from 'react-filepond'
import { Message, RobotMessage } from '../message'
import { ChatRoom, CompanyChatOpenInfo, Type, Visitor } from '../types'
import 'filepond/dist/filepond.min.css'
import { ChatMessage, Client, SentMessage } from '@market-up/chat-core'
import {
  groupMessagesByDay,
  messageQueue,
  newMessageNotification,
  useForceUpdate,
} from '../utils'
import {
  Date,
  EmojiPicker,
  Empty,
  FileContainer,
  Input,
  InputContainer,
  MessagesContainer,
  SendButton
} from './components'
import dayjs from 'dayjs'
import { initChat } from '../backend'

const INSTANT_SCROLL_THRESHOLD = 1000
const isSentMessage = (message: ChatMessage | SentMessage): message is SentMessage => 'message' in message
const getEpochSeconds = (message: ChatMessage | SentMessage): number => {
  if (isSentMessage(message)) {
    return Math.floor(message.sentTime.getTime() / 1000)
  } else {
    return dayjs(message.sendTime).unix()
  }
}

export interface ChatPageProps {
  companyInfo: CompanyChatOpenInfo
  visitor: Visitor
  type?: Type
  chatRoom: ChatRoom
}
export const ChatPage: React.FC<ChatPageProps> = ({ chatRoom, type, visitor,companyInfo }) => {
  const messageContainerRef = useRef<HTMLDivElement>(null)
  const filePondRef = useRef<FilePond>(null)
  const [file, setFile] = useState<File>()
  const [input, setInput] = useState('')
  const [client, setClient] = useState<Client>()
  const rerender = useForceUpdate()
  useEffect(() => {
    if (visitor && chatRoom) {
      const client = new Client({
        accountKey: visitor.accountKey,
        chatRoomId: chatRoom.chatRoomId as any,
        role: 'account',
        roomKey: ''
      })
      client.on('messageSent', () => rerender())
      client.on('messageReceived', () => rerender())
      client.on('messageTimeout', () => rerender())
      client.on('messageRead', () => rerender())
      client.on('newMessage', (message: ChatMessage) => {
        rerender()
        if (document.visibilityState === 'visible') {
          client.confirmRead(message)
        } else {
          newMessageNotification({ title: '您有一条新的回复', icon: companyInfo.squareLogo }, () => window.focus())
        }
      })
      client.on('clientError', console.log)
      client.on('initialized', () => {
        setClient(client)
        client.oldMessages
          .filter(message => message.messageRead === 0)
          .forEach(message => client.confirmRead(message))
      })
      while (messageQueue.length) {
        const message = messageQueue.shift()
        if (message) {
          client.sendMessage(message)
        }
      }
      return () => client.disconnect()
    }
  }, [visitor, chatRoom, setClient, rerender])
  const sendMessage = () => {
    if (client) {
      if (file) {
        client.sendMessage(file)
        setFile(undefined)
      }
      if (input) {
        client.sendMessage(input)
        setInput('')
      }
    }
  }
  useEffect(() => {
    const container = messageContainerRef.current
    if (container) {
      const { scrollHeight, scrollTop, clientHeight } = container
      const scrollDistance = scrollHeight - scrollTop - clientHeight
      container.scrollTo({
        top: scrollHeight,
        behavior: scrollDistance > INSTANT_SCROLL_THRESHOLD ? 'auto' : 'smooth'
      })
    }
  }, [client?.messages.length])
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        client!.confirmReadReceived()
      }
    }
    if (client) {
      document.addEventListener('visibilitychange', handler)
      return () => document.removeEventListener('visibilitychange', handler)
    }
  }, [client])
  // grouping on every render may be inefficient, but it's either this or managing messages externally
  const messages = groupMessagesByDay(client?.messages ?? [])
  return (chatRoom && visitor)
    ? (
      <>
        <MessagesContainer ref={messageContainerRef}>
          <RobotMessage robotMsg={companyInfo.greet} show={!!companyInfo.greet} />
          {messages.map(dayOfMessages => (
            <React.Fragment key={dayOfMessages[0].messageId + 'group'}>
              <Date date={dayjs(dayOfMessages[0].sendTime)} />
              {dayOfMessages.map(message => (
                <Message
                  key={message.messageId}
                  message={message}
                  resendMessage={message => client?.resendMessage(message.uuid)}
                />
              ))}
            </React.Fragment>
          ))}
        </MessagesContainer>
        <FileContainer visible={!!file}>
          <FilePond
            ref={filePondRef}
            allowMultiple={false}
            maxFiles={1}
            onupdatefiles={([fileItem]) => {
              setFile(fileItem?.file as File)
            }}
          />
        </FileContainer>
        <InputContainer>
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onPaste={e => {
              const files = e.nativeEvent.clipboardData?.files
              const filePond = filePondRef.current
              if (files?.length && filePond) {
                const file = files[0]
                filePond.addFile(file)
                e.preventDefault()
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.stopPropagation()
                e.preventDefault()
                sendMessage()
              }
            }}
          />
          <EmojiPicker onClick={emoji => setInput(input => input + emoji.unicode)} />
          <SendButton onClick={() => sendMessage()} />
        </InputContainer>
      </>
    )
    : <Empty />
}
